import React, {useContext} from 'react';

import {DropdownMenuBase} from './dropdown-menu-base';
import {DropdownMenuContext} from './dropdown-menu-context';
import {useDropdownMenu} from './use-dropdown-menu';

import type {UseDropdownMenuReturn} from './use-dropdown-menu';
import type {Placement} from '@floating-ui/react';
import type {ReactNode} from 'react';


const placements: Record<string, Placement[]> = {
    right: ['right-start', 'right-end', 'left-start', 'left-end'],
    left: ['left-start', 'left-end', 'right-start', 'right-end'],
};

type Props = {
    /** `<DropdownMenuItem>` nodes rendered inside the nested floating panel. */
    children: ReactNode,
    /** Which side the floating panel opens toward. Defaults to `"right"`. */
    nestedListDirection?: 'right' | 'left',
    /**
     * Render prop for the row that acts as the sub-menu trigger. Receives
     * `anchorRef` and `getAnchorProps` — spread them onto a `<FloatingMenuItem>`
     * so the nested panel attaches and responds to hover/focus correctly.
     */
    anchor: (props: {
        anchorRef: UseDropdownMenuReturn['anchorRef'],
        getAnchorProps: UseDropdownMenuReturn['getAnchorProps'],
    }) => ReactNode,
};

/**
 * Internal component that renders a hoverable sub-menu panel anchored to a
 * parent `<DropdownMenuItem>`. Inherits `type`, `maxHeight`, and `maxWidth`
 * from `DropdownMenuContext` so nested panels respect the same sizing constraints
 * as the root menu. Placement is selected from the preferred side list derived
 * from `nestedListDirection`, falling back to the opposite side when space is
 * constrained.
 */
export const NestedMenu = (props: Props) => {
    const {
        children,
        nestedListDirection = 'right',
        anchor,
    } = props;

    const {type, maxHeight, maxWidth} = useContext(DropdownMenuContext);
    const {
        anchorRef,
        dropdownRef,
        getAnchorProps,
        getDropdownMenuProps,
    } = useDropdownMenu({
        allowedPlacements: placements[nestedListDirection],
        hoverEnabled: true,
        focusEnabled: false,
        type,
        maxHeight,
        maxWidth,
    });

    return (
        <>
            {anchor({anchorRef, getAnchorProps})}
            <DropdownMenuBase
                {...getDropdownMenuProps()}
                ref={dropdownRef}>
                {children}
            </DropdownMenuBase>
        </>
    );
};
